/**
 * Pull a concrete length (`width`/`minWidth`) out of a legacy `sx` prop, if present. Only a plain
 * object `sx` with a static string/number value is honored; responsive/array/callback `sx` values
 * are ignored since they encode MUI theme semantics we do not carry into the FPL path.
 */
function extractSxLength(sx: unknown, key: 'width' | 'minWidth'): string | number | undefined {
  if (sx && typeof sx === 'object' && !Array.isArray(sx)) {
    const value = (sx as Record<string, unknown>)[key];
    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }
  }
  return undefined;
}

export function extractSxWidth(sx: unknown): string | number | undefined {
  return extractSxLength(sx, 'width');
}

export function extractSxMinWidth(sx: unknown): string | number | undefined {
  return extractSxLength(sx, 'minWidth');
}

/**
 * Mirrors MUI's numeric width transform: a number <= 1 is a percentage, otherwise px.
 */
export function toCssWidth(width: string | number | undefined): string | undefined {
  if (typeof width === 'string') return width;
  if (typeof width === 'number') return width <= 1 && width !== 0 ? `${width * 100}%` : `${width}px`;
  return undefined;
}

/** FPL inputs accept only string values; normalize the looser MUI value prop. */
export function toStringValue(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.join(',');
  return undefined;
}
